import { useState } from 'react';
import {
  deleteReview,
  generateReview,
  getReview,
  isAutoEnabled,
  NoDataError,
} from '../../sync/review';
import { useAsync } from '../hooks/useAsync';
import { useDataVersion } from '../../db/events';
import type { WeeklyReview } from '../../domain/types';

/** 週ビューの先頭に置く、その週のAIレビュー。 */
export function WeeklyReviewCard({ week, isPast }: { week: string; isPast: boolean }) {
  const version = useDataVersion();
  const review = useAsync<WeeklyReview | undefined>(() => getReview(week), [week, version], undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await generateReview(week);
    } catch (e) {
      setError(
        e instanceof NoDataError
          ? 'この週には記録がないため、レビューを作れません。'
          : e instanceof Error
            ? e.message
            : '生成に失敗しました。',
      );
    } finally {
      setBusy(false);
    }
  };

  if (!review) {
    if (!isPast) return null;
    return (
      <div className="review-card is-empty">
        <div className="review-head">
          <h2>週のふりかえり</h2>
          <button className="today-btn" onClick={run} disabled={busy}>
            {busy ? '作成中…' : 'AIに作ってもらう'}
          </button>
        </div>
        {error && <p className="review-error">{error}</p>}
        {!isAutoEnabled() && !error && (
          <p className="muted">
            設定で自動生成を有効にすると、週が終わったあとアプリを開いたときに自動で作られます。
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="review-card">
      <div className="review-head">
        <h2>週のふりかえり</h2>
        <span className="muted review-model">{review.model}</span>
        <button className="icon-btn" onClick={run} disabled={busy} title="作り直す">
          {busy ? '…' : '↻'}
        </button>
        <button
          className="icon-btn danger"
          title="削除"
          onClick={() => {
            if (confirm('このふりかえりを削除します。よろしいですか？')) deleteReview(week);
          }}
        >
          ✕
        </button>
      </div>
      {error && <p className="review-error">{error}</p>}

      <p className="review-summary">{review.summary}</p>

      {review.aligned.length > 0 && (
        <div className="review-block">
          <span className="review-label good">ゴールに沿って動けたこと</span>
          <ul>
            {review.aligned.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </div>
      )}

      {review.improve.length > 0 && (
        <div className="review-block">
          <span className="review-label">次に変えるとよいこと</span>
          <ul>
            {review.improve.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </div>
      )}

      {review.nextAction && (
        <div className="review-action">
          <span className="review-label">来週試すひとつ</span>
          <p>{review.nextAction}</p>
        </div>
      )}
    </div>
  );
}
